/**
 * Parse context — builds the shared state needed by all sub-parsers.
 *
 * The ParseContext carries resolved styles, numbering definitions,
 * relationships, and comments through the entire parse tree.
 */

import {
  ParseContext,
  StyleDefinition,
  RunProperties,
  ParagraphProperties,
} from "./types";
import {
  DocxPackage,
  extractRelationships,
  extractComments,
  extractCommentReplyMap,
  extractStyles,
  extractNumbering,
} from "./zipReader";

let nodeIdCounter = 0;

/** Generate a unique node ID compatible with UniqueIdExtension format. */
function defaultGenerateNodeId(): string {
  nodeIdCounter++;
  const timestamp = Date.now();
  const random = Math.floor(Math.random() * 1000000);
  return `node-${timestamp}-${random}-${nodeIdCounter}`;
}

/**
 * Build a ParseContext from a DocxPackage.
 *
 * @param pkg - The parsed DOCX package
 * @param generateNodeId - Optional custom node ID generator (for testing or UniqueIdExtension integration)
 */
export function buildParseContext(
  pkg: DocxPackage,
  generateNodeId?: () => string
): ParseContext {
  const [styles, styleNumIdMap] = extractStyles(pkg.stylesXml);
  const numbering = extractNumbering(pkg.numberingXml);
  const relationships = extractRelationships(pkg.relationshipsXml);
  const comments = extractComments(pkg.commentsXml);
  const commentReplyMap = extractCommentReplyMap(
    pkg.commentsExtendedXml,
    comments
  );

  return {
    styles,
    numbering,
    relationships,
    comments,
    commentReplyMap,
    styleNumIdMap,
    images: new Map(), // populated lazily in imageParser
    generateNodeId: generateNodeId || defaultGenerateNodeId,
  };
}

/**
 * Resolve a paragraph style to its full properties by walking the basedOn chain.
 * Returns merged properties with the most specific style winning.
 */
export function resolveStyleProperties(
  styleId: string,
  ctx: ParseContext,
  visited = new Set<string>()
): { runProperties: RunProperties; paragraphProperties: ParagraphProperties } {
  const result: {
    runProperties: RunProperties;
    paragraphProperties: ParagraphProperties;
  } = {
    runProperties: {},
    paragraphProperties: {},
  };

  const style = ctx.styles.get(styleId);
  if (!style || visited.has(styleId)) return result;
  visited.add(styleId);

  // First resolve parent style
  if (style.basedOn) {
    const parent = resolveStyleProperties(style.basedOn, ctx, visited);
    Object.assign(result.runProperties, parent.runProperties);
    Object.assign(result.paragraphProperties, parent.paragraphProperties);
  }

  // Then overlay this style's properties (more specific wins)
  if (style.runProperties) {
    Object.assign(result.runProperties, style.runProperties);
  }
  if (style.paragraphProperties) {
    Object.assign(result.paragraphProperties, style.paragraphProperties);
  }

  return result;
}

/**
 * Detect if a paragraph style is a heading, and return the heading level (1-6).
 * Returns null if not a heading.
 *
 * Checks:
 * 1. Standard "Heading1"-"Heading6" or "heading 1"-"heading 6" style IDs
 * 2. Style name containing "heading" with a number
 * 3. outlineLevel property (0 = heading 1, 5 = heading 6)
 *
 * `isNumbered` — whether the paragraph carries list numbering. When true, the
 * outlineLevel signal (check 3) is ignored: a numbered paragraph is a list item,
 * and its outlineLvl is navigation/TOC metadata, NOT a render-as-heading flag.
 * (Legal/contract clause styles such as "MF Para subclause 1" set outlineLvl on
 * numbered clauses so they appear in the navigation pane — they are body text,
 * not headings. Explicit Heading 1-6 styles still win via checks 1/2.)
 */
export function detectHeadingLevel(
  styleId: string | undefined,
  pProps: ParagraphProperties,
  ctx: ParseContext,
  isNumbered: boolean = false
): number | null {
  // Check by styleId pattern
  if (styleId) {
    // "Heading1", "Heading2", ..., "Heading6"
    const match = styleId.match(/^[Hh]eading\s*(\d)$/);
    if (match) {
      const level = parseInt(match[1], 10);
      if (level >= 1 && level <= 6) return level;
    }

    // Also check style name for localized/variant names
    const style = ctx.styles.get(styleId);
    if (style?.name) {
      const nameMatch = style.name.match(/heading\s*(\d)/i);
      if (nameMatch) {
        const level = parseInt(nameMatch[1], 10);
        if (level >= 1 && level <= 6) return level;
      }
    }
  }

  // Check outlineLevel (0-based: 0 = heading 1) — but NOT for numbered paragraphs,
  // where outlineLvl is TOC/navigation metadata rather than a heading signal.
  if (
    !isNumbered &&
    pProps.outlineLevel !== undefined &&
    pProps.outlineLevel >= 0 &&
    pProps.outlineLevel <= 5
  ) {
    return pProps.outlineLevel + 1;
  }

  return null;
}
